import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { COLORS } from '@/lib/colors';

// Using the official US News methodology weights with new color scheme
const METHODOLOGY_DATA = [
  { name: 'Outcomes', weight: 52, color: COLORS.primary.darkBlue },         // Highest weight
  { name: 'Expert Opinion', weight: 20, color: COLORS.primary.mediumBlue }, // Second highest
  { name: 'Faculty Resources', weight: 11, color: COLORS.primary.lightBlue },
  { name: 'Financial Resources', weight: 8, color: COLORS.supporting.turquoise },
  { name: 'Student Excellence', weight: 5, color: COLORS.supporting.mint },
  { name: 'Faculty Research', weight: 4, color: COLORS.accent.yellow }      // Lowest weight
];

const getMethodologyDescription = (category: string): string => {
  const descriptions: Record<string, string> = {
    'Outcomes': 'Graduation rates, retention rates, social mobility, and graduate indebtedness',
    'Expert Opinion': 'Peer assessment scores from top academics',
    'Faculty Resources': 'Class size, faculty salary, proportion of full-time faculty',
    'Financial Resources': 'Per-student spending on academics',
    'Student Excellence': 'Standardized test scores and high school class standing',
    'Faculty Research': 'Research activity and scholarly impact'
  };
  return descriptions[category] || '';
};

export function MethodologyBreakdownChart() {
  const options = {
    chart: {
      type: 'donut',
      fontFamily: 'inherit',
      animations: {
        enabled: true,
        speed: 500,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      }
    },
    colors: METHODOLOGY_DATA.map(item => item.color),
    labels: METHODOLOGY_DATA.map(item => item.name),
    dataLabels: {
      enabled: true,
      formatter: function (val: number, opts: any) {
        return `${METHODOLOGY_DATA[opts.seriesIndex].weight}%`;
      },
      style: {
        fontSize: '14px',
        fontFamily: 'inherit',
        fontWeight: 500,
        colors: ['#fff']
      },
      dropShadow: {
        enabled: true,
        color: '#000',
        top: 1,
        left: 1,
        blur: 2,
        opacity: 0.2
      }
    },
    plotOptions: {
      pie: {
        donut: {
          size: '60%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '14px',
              fontFamily: 'inherit',
              color: '#304758'
            },
            value: {
              show: true,
              fontSize: '16px',
              fontFamily: 'inherit',
              color: '#304758',
              formatter: function (val: number) {
                return val + '%';
              }
            },
            total: {
              show: true,
              label: 'Total Weight',
              fontSize: '14px',
              fontFamily: 'inherit',
              color: '#304758',
              formatter: function () {
                return '100%';
              }
            }
          }
        }
      }
    },
    legend: {
      position: 'right',
      fontSize: '13px',
      fontFamily: 'inherit',
      labels: {
        colors: '#304758'
      },
      markers: {
        width: 12,
        height: 12,
        radius: 6
      },
      formatter: function(seriesName: string, opts: any) {
        return [
          seriesName,
          ' - ',
          METHODOLOGY_DATA[opts.seriesIndex].weight + '%'
        ];
      }
    },
    tooltip: {
      enabled: true,
      custom: function({ series, seriesIndex, w }: any) {
        const category = METHODOLOGY_DATA[seriesIndex].name;
        return `
          <div class="p-4 bg-white rounded-lg shadow-lg border">
            <div class="font-medium" style="color: ${METHODOLOGY_DATA[seriesIndex].color}">${category}</div>
            <div class="text-gray-600">Weight: ${METHODOLOGY_DATA[seriesIndex].weight}%</div>
            <div class="text-sm mt-1 text-gray-500">${getMethodologyDescription(category)}</div>
          </div>
        `;
      }
    },
    stroke: {
      width: 2,
      colors: ['#fff']
    },
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          width: 320
        },
        legend: {
          position: 'bottom'
        }
      }
    }]
  };

  const series = METHODOLOGY_DATA.map(item => item.weight);

  return (
    <div className="w-full h-[400px]">
      <ReactApexChart
        options={options}
        series={series}
        type="donut"
        height={400}
      />
    </div>
  );
} 